// filepath: apps/web/src/editors/form-builder/FormBuilderPage.tsx
//
// FR-UI-BLD-1 AC-1 / AC-2 / AC-4 — composition root for the form
// builder. Three-pane layout: palette (left) + canvas (center) +
// settings panel (right). Save button at top fires the save
// mutation; HTTP 422 returns inline errors next to the offending
// field via the InlineFieldError / inline `data-field-id` hook.

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FormFieldPalette } from "./FormFieldPalette";
import { FormCanvas } from "./FormCanvas";
import { FormSettingsPanel } from "./FormSettingsPanel";
import { useEntityTypes, useFormSave } from "./queries";
import { classifyFormBuilderError } from "./types";
import type {
  FieldDefinition,
  FieldType,
  FormDraft,
  ValidationErrorWire,
} from "./types";

interface FormBuilderPageProps {
  environmentKind: string;
}

const EMPTY_DRAFT: FormDraft = {
  entity_type: "",
  fields: {},
};

export function FormBuilderPage({
  environmentKind: _environmentKind,
}: FormBuilderPageProps): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [draft, setDraft] = useState<FormDraft>(EMPTY_DRAFT);
  const [selectedFieldId, setSelectedFieldId] = useState<string | undefined>();
  const [errors, setErrors] = useState<ValidationErrorWire[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);

  const entityTypesQuery = useEntityTypes();
  const saveMutation = useFormSave();

  function ensureFieldId(): string {
    let id = `field_${Object.keys(draft.fields).length + 1}`;
    while (draft.fields[id]) {
      id = `${id}_${Math.random().toString(36).slice(2, 6)}`;
    }
    return id;
  }

  function addField(fieldType: FieldType): string {
    const id = ensureFieldId();
    const definition: FieldDefinition = { field_type: fieldType };
    setDraft((d) => ({ ...d, fields: { ...d.fields, [id]: definition } }));
    setSelectedFieldId(id);
    return id;
  }

  function removeField(fieldId: string): void {
    setDraft((d) => {
      const { [fieldId]: _removed, ...rest } = d.fields;
      void _removed;
      return { ...d, fields: rest };
    });
    if (selectedFieldId === fieldId) {
      setSelectedFieldId(undefined);
    }
  }

  function updateSelectedField(next: FieldDefinition): void {
    if (!selectedFieldId) return;
    setDraft((d) => ({
      ...d,
      fields: { ...d.fields, [selectedFieldId]: next },
    }));
  }

  async function handleSave(): Promise<void> {
    setErrors([]);
    setSaveError(null);
    try {
      const response = await saveMutation.mutateAsync({
        entity_type: draft.entity_type,
        fields: draft.fields,
      });
      void navigate(`/admin/forms?new=${response.form_id}`);
    } catch (err) {
      const classified = classifySaveError(err);
      if (classified.kind === "validation_failed") {
        setErrors(classified.validationErrors ?? []);
      } else {
        setSaveError(classified.message);
      }
    }
  }

  return (
    <div className="form-builder-page" data-testid="form-builder-page">
      <header className="form-builder-page__header">
        <h1 className="form-builder-page__title">
          {t("admin.builder.page_title")}
        </h1>
        <button
          type="button"
          className="form-builder-page__save"
          onClick={() => void handleSave()}
          disabled={saveMutation.isPending}
          data-testid="form-builder-save"
        >
          {saveMutation.isPending
            ? t("admin.builder.save.saving")
            : t("admin.builder.save.cta")}
        </button>
      </header>
      {saveError && (
        <div
          role="alert"
          className="form-builder-page__save-error"
          data-testid="form-builder-save-error"
        >
          {saveError}
        </div>
      )}
      <div className="form-builder-page__layout">
        <FormFieldPalette
          onDragFieldType={(ft) => {
            // Drag is gesture-only; the drop handler on the
            // canvas actually creates the field. We pre-call
            // addField via the drop event forwarding.
            void ft;
          }}
        />
        <FormCanvas
          fields={draft.fields}
          onSelectField={setSelectedFieldId}
          onRemoveField={removeField}
          selectedFieldId={selectedFieldId}
          errors={errors}
          onDropField={(ft) => {
            addField(ft as FieldType);
          }}
        />
        <FormSettingsPanel
          draft={draft}
          selectedFieldId={selectedFieldId}
          onChangeSelectedField={updateSelectedField}
          onChangeEntityType={(next) => {
            setDraft((d) => ({ ...d, entity_type: next }));
          }}
          entityTypes={entityTypesQuery.data ?? []}
          errors={errors}
        />
      </div>
    </div>
  );
}

function classifySaveError(
  err: unknown,
): ReturnType<typeof classifyFormBuilderError> {
  const e = err as Error & { body?: unknown; status?: number };
  const status = typeof e?.status === "number" ? e.status : 500;
  const body = e?.body ?? e?.message ?? "";
  return classifyFormBuilderError(status, body);
}
